"use client";

import { FieldWrapper } from "@/components/form/FieldWrapper/FieldWrapper";
import { useId } from "react";
import {
	Control,
	Controller,
	FieldPath,
	FieldValues,
	RegisterOptions,
	UseControllerProps,
	useFormState,
} from "react-hook-form";
import { SelectInput } from "../../input/SelectInput/SelectInput";
import styles from "./DropdownField.module.scss";
type Option = {
	label: string;
	value: string;
};

export interface DropdownFieldProps<T extends FieldValues, TPath extends FieldPath<T>> {
	control: Control<T>;
	name: TPath;
	label: string;
	hideLabel?: boolean;
	options?: Option[];
	className?: string;
	placeholder?: string;
	registerOptions?: UseControllerProps<T, TPath>["rules"];
	inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
	slotAfterInput?: React.ReactNode;
	ariaDescribedBy?: string;
	disabled?: boolean;
}

export const DropdownField = <T extends FieldValues, TPath extends FieldPath<T>>({
	control,
	name,
	label,
	hideLabel,
	options,
	className,
	placeholder,
	registerOptions,
	inputProps,
	slotAfterInput,
	ariaDescribedBy,
	disabled,
}: DropdownFieldProps<T, TPath>) => {
	const id = useId();
	const helpId = id + "-help";
	const errorId = id + "-error";

	// const { register } = useFormContext();
	const { errors } = useFormState();

	return (
		<FieldWrapper
			{...{
				name,
				label,
				hideLabel,
				required: registerOptions?.required as boolean,
				labelFor: id,
				className,
				errorId,
			}}
		>
			<div className={styles["dropdown-wrapper"]}>
				<Controller
					control={control}
					name={name}
					rules={registerOptions}
					render={({ field }) => {
						return (
							<SelectInput
								name={name}
								options={options}
								value={field.value}
								aria-invalid={!!errors[name]}
								aria-required={!!registerOptions?.required}
								aria-describedby={
									[errors[name] ? errorId : "", ariaDescribedBy].filter(v => v).join(" ") ||
									undefined
								}
								onValueChange={(...args) => {
									field.onChange(...args);
								}}
								placeholder={placeholder}
								disabled={field.disabled || disabled}
								// The underlying <select> was not getting its value set, so the required flag here would trigger even when the controlled field has a value.
								// Commenting it out for now
								// required={registerOptions?.required as boolean}
							/>
						);
					}}
				/>
				{slotAfterInput}
			</div>
		</FieldWrapper>
	);
};
